import { Container, Graphics, Sprite } from 'pixi.js';
import gsap from 'gsap';
import { COLORS, FONT, SCREEN, SHAPE, TIMING } from '../config/tokens';
import type { Layout, Screen } from '../render/stage';
import { Fx, reducedMotion } from '../render/fx';
import { frame } from '../assets/loader';
import { dogFrame, type DogId } from '../assets/names';
import { after, button, label, roundedPanel } from '../ui/components';
import { dogInfo } from '../meta/dogs';
import { sound } from '../audio/sound';
import { haptic } from '../audio/haptics';

/**
 * Mockup 28: "Honey found a home!" – the freshly bathed dog walks through
 * the open café door. Door and garden are drawn in code (no asset yet).
 */
export class DogArrivalScreen implements Screen {
  readonly view = new Container();
  private readonly bg = new Graphics();
  private readonly scene = new Container();
  private readonly fx = new Fx();
  private readonly ui = new Container();
  private l?: Layout;
  private title!: Container;
  private welcome!: Container;
  private dogSprite!: Sprite;

  constructor(private readonly dog: DogId, private readonly onDone: () => void) {
    this.view.addChild(this.bg, this.scene, this.fx.view, this.ui);
    this.buildScene();
    const name = dogInfo(dog).name;
    // title panel: two lines max (§3)
    const title = new Container();
    const t = label(`${name} found\na home!`, FONT.size.celebration + 6, { weight: FONT.weight.heading });
    t.style.align = 'center';
    const w = Math.max(640, t.width + 90);
    const h = t.height + 60;
    const panel = roundedPanel(w, h, { radius: 36 });
    panel.position.set(-w / 2, -h / 2);
    title.addChild(panel, t);
    title.position.set(SCREEN.width / 2, 0);
    this.title = title;
    this.ui.addChild(title);
    const welcome = button(`Welcome, ${name}!`, 640, 'primary', () => this.onDone(), 104);
    this.welcome = welcome;
    welcome.alpha = 0;
    welcome.eventMode = 'none';
    this.ui.addChild(welcome);
  }

  destroy(): void {
    this.fx.destroy();
  }

  private buildScene(): void {
    const s = this.scene;
    // café door arch with an open door leaf; garden visible outside
    const arch = new Graphics();
    const dw = 560;
    const dh = 860;
    const x0 = -dw / 2;
    arch.roundRect(x0 - 40, -dh - 40, dw + 80, dh + 60, dw / 2 + 40).fill(COLORS.wood).stroke({ width: SHAPE.outline, color: COLORS.cocoa });
    arch.roundRect(x0, -dh, dw, dh, dw / 2).fill(COLORS.sky).stroke({ width: SHAPE.outline, color: COLORS.cocoa });
    arch.rect(x0 + 4, -300, dw - 8, 296).fill(COLORS.sage);
    for (let i = 0; i < 6; i++) arch.circle(x0 + 60 + i * 90, -300, 60).fill(COLORS.sage);
    arch.rect(x0 + 4, -120, dw - 8, 116).fill(COLORS.latte);
    // open door leaf on the right
    arch.poly([dw / 2 - 4, -dh + 150, dw / 2 + 150, -dh + 110, dw / 2 + 150, 30, dw / 2 - 4, 0]).fill(COLORS.wood).stroke({ width: SHAPE.outline, color: COLORS.cocoa });
    arch.poly([dw / 2 + 30, -dh + 220, dw / 2 + 120, -dh + 195, dw / 2 + 120, -420, dw / 2 + 30, -400]).fill(COLORS.sky).stroke({ width: 3, color: COLORS.cocoa });
    arch.circle(dw / 2 + 125, -380, 11).fill(COLORS.butter).stroke({ width: 3, color: COLORS.cocoa });
    s.addChild(arch);
    const dog = new Sprite(frame(dogFrame(this.dog, 'happy')));
    dog.anchor.set(0.5, 1);
    this.dogSprite = dog;
    s.addChild(dog);
  }

  private play(): void {
    const dog = this.dogSprite;
    const full = 600 / dog.texture.height;
    const d = reducedMotion ? 0.3 : TIMING.arrival / 1000 + 0.6;
    dog.scale.set(full * 0.55);
    dog.y = -100;
    dog.alpha = reducedMotion ? 0 : 1;
    gsap.to(dog, { alpha: 1, duration: 0.3 });
    // walk in: grow and come forward with a little bob
    gsap.to(dog.scale, { x: full, y: full, duration: d, ease: 'power1.out' });
    gsap.to(dog, { y: 60, duration: d, ease: 'power1.out' });
    if (!reducedMotion) gsap.fromTo(dog, { rotation: -0.05 }, { rotation: 0.05, duration: 0.18, yoyo: true, repeat: Math.round(d / 0.18) - 1, ease: 'sine.inOut', onComplete: () => void (dog.rotation = 0) });
    after(this.view, d * 1000, () => {
      sound.play('praise');
      haptic('success');
      const p = this.view.toLocal(dog.toGlobal({ x: 0, y: -dog.texture.height * 0.6 }));
      this.fx.burst(p.x, p.y, { frame: 'vfx_heart', count: 6, speed: 260, gravity: -80, size: 50, life: 1 });
      this.fx.burst(p.x, p.y, { frame: 'vfx_sparkle', count: 6, speed: 300, gravity: 0, size: 40 });
      const btn = this.welcome;
      gsap.to(btn, { alpha: 1, duration: 0.3 });
      btn.eventMode = 'static';
      // gentle idle: breathing
      gsap.to(dog.scale, { y: full * 1.02, x: full * 0.99, duration: 1.5, yoyo: true, repeat: -1, ease: 'sine.inOut' });
    });
  }

  layout(l: Layout): void {
    const first = !this.l;
    this.l = l;
    this.bg.clear().rect(l.left, 0, l.right - l.left, l.bottom).fill(COLORS.mint)
      .rect(l.left, l.bottom * 0.72, l.right - l.left, l.bottom * 0.28).fill(COLORS.latte);
    this.scene.position.set(SCREEN.width / 2, l.bottom * 0.72);
    this.title.y = l.safeTop + 110;
    this.welcome.position.set(SCREEN.width / 2, l.safeBottom - 90);
    if (first) this.play();
  }
}
